import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../config";
import { withSavedData } from "../v2admin/offline/withSavedData";
import { clearSaved } from "../v2admin/offline/savedData";

// Single-flight guard for refresh: ensures only one refresh runs at a time
let refreshPromise = null;

/**
 * Returns a baseQuery function that automatically refreshes the access token
 * when a 401 response is received. It also forwards credentials (cookies) so
 * the refresh cookie is included.
 *
 * @param {string} relativeBaseUrl Example: "/audio/". Use empty string for root.
 */
export const prepareBaseQuery = (relativeBaseUrl = "") => {
  const resolveBaseUrl = () => {
    if (typeof window !== "undefined" && window.__API_BASE_URL) {
      return window.__API_BASE_URL;
    }
    return config.api.baseURL;
  };

  // Freeze the base URL at creation time so the "refresh" request cannot
  // accidentally go to a different backend than the original request.
  const baseUrl = resolveBaseUrl();

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: `${baseUrl}${relativeBaseUrl}`,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  });

  const withReauth = async (args, api, extraOptions) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      // Some endpoints can return 401 for reasons unrelated to auth session,
      // e.g. Gmail reconnect required. Do not try /auth/refresh in that case.
      const errorCode = result?.error?.data?.code;
      if (errorCode === "GMAIL_RECONNECT_REQUIRED") {
        return result;
      }

      // Avoid attempting to refresh while already calling refresh
      const isRefreshCall =
        typeof args === "object" &&
        args?.url &&
        (args.url.endsWith("/auth/refresh") || args.url === "/auth/refresh");

      if (!isRefreshCall) {
        const rootBaseQuery = fetchBaseQuery({
          baseUrl,
          credentials: "include",
        });

        if (!refreshPromise) {
          refreshPromise = (async () => {
            const existingRefreshToken =
              typeof localStorage !== "undefined"
                ? localStorage.getItem("refreshToken")
                : null;

            if (!existingRefreshToken) {
              if (typeof localStorage !== "undefined") {
                localStorage.removeItem("token");
              }
              // Signed out: drop the offline saved copies too.
              clearSaved();
              throw new Error("No refresh token available");
            }

            const refreshResult = await rootBaseQuery(
              {
                url: "/auth/refresh",
                method: "POST",
                body: { refreshToken: existingRefreshToken },
              },
              api,
              extraOptions
            );

            if (refreshResult?.data?.token && refreshResult.data.refreshToken) {
              if (typeof localStorage !== "undefined") {
                localStorage.setItem("token", refreshResult.data.token);
                localStorage.setItem(
                  "refreshToken",
                  refreshResult.data.refreshToken
                );
              }
              return true;
            }

            if (typeof localStorage !== "undefined") {
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
            }
            // Signed out: drop the offline saved copies too.
            clearSaved();
            throw new Error("Refresh failed");
          })().finally(() => {
            refreshPromise = null;
          });
        }

        try {
          await refreshPromise;
          // Retry original request after successful refresh
          result = await rawBaseQuery(args, api, extraOptions);
        } catch {
          // Leave result as 401; caller can redirect to login
        }
      }
    }

    return result;
  };

  // Admin pages only: saved copies of the admin reads for offline use, and
  // no writes while offline (v2admin/offline/withSavedData.js).
  return withSavedData(withReauth);
};
